'use client';
// lib/analytics.ts
/**
 * Unified Analytics Helper
 * This file provides a single interface for tracking events across
 * Google Analytics 4 (GA4) and Comscore.
 * It abstracts away the individual calls to each analytics platform.
 */

import { trackGA4Event, trackGA4PageView, trackGA4Click } from './ga4';
import { trackComscorePageView, trackComscoreClick, trackComscoreEvent } from './comscore';
import { getDeviceId, getSessionId, getUUID, getZonePreference } from './helpers';

interface AnalyticsContextInput {
  session_id?: string;
  uid?: string;
  deviceId?: string;
  parsedUserData?: {
    region?: string;
    tenant?: string;
    sub_admin_area?: string;
    sub_district_code?: string;
    // sub_district?: string;
  };
  customData?: {
    timestamp?: number;
  };
  utm_obj?: {
    utm_source?: string;
    utm_medium?: string;
    session_source?: string;
    session_medium?: string;
  };
  feed_id?: string;
}

export function getAnalyticsContext({
  utm_obj = {},
}: AnalyticsContextInput) {
  const connection = typeof window !== 'undefined' && window.navigator && (window.navigator as any).connection;
  const parsedUserData = getZonePreference();
  return {
    network_speed: connection && connection.downlink ? connection.downlink * 25 : undefined,
    network_type: connection && connection.effectiveType,
    session_id: getSessionId(),
    uid: getUUID(),
    device_id: getDeviceId(),
    device_region: parsedUserData?.region,
    device_tenant: parsedUserData?.tenant,
    location_district: parsedUserData?.sub_admin_area,
    location_code: parsedUserData?.sub_district_code,
    location_sub_district_code: parsedUserData?.sub_district_code,
    timestamp: Date.now(),
    utm_source: utm_obj.utm_source,
    utm_medium: utm_obj.utm_medium,
    session_source: utm_obj.session_source,
    session_medium: utm_obj.session_medium,
    app_version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
  };
}

/**
 * Tracks a page view across both GA4 and Comscore.
 *
 * @param {string} url - The full URL of the page being viewed.
 * @param {string} title - The title of the page being viewed.
 * @param {object} [additionalLabels] - Optional additional labels specific to Comscore pageview.
 */
export const trackPageView = (
  url: string,
  title: string,
  additionalLabels?: { [key: string]: string | number }
) => {
  // Track page view for GA4
  // Note: @next/third-parties/google/GoogleAnalytics component often handles this automatically
  // for standard navigations if Enhanced Measurement is enabled.
  const analyticsContext = getAnalyticsContext({});
  trackGA4PageView(url, title, {...additionalLabels, ...analyticsContext});

  // Track page view for Comscore
  trackComscorePageView(url, title, {...additionalLabels, ...analyticsContext});

};

/**
 * Tracks a click event across both GA4 and Comscore.
 *
 * @param {string} elementId - The ID of the clicked element.
 * @param {string} [category='Interaction'] - The category of the click.
 * @param {string} [action='Click'] - The action performed.
 * @param {string} [label] - An optional label for more detail.
 */
export const trackClick = (
  elementId: string,
  category: string = 'Interaction',
  action: string = 'Click',
  label?: string,
  additionalParams?: { [key: string]: any }
) => {


  const additionalContext = getAnalyticsContext({});
  // Track click for GA4
  trackGA4Click(elementId, category, action, label, {...additionalParams,  ...additionalContext});

  // Track click for Comscore
  trackComscoreClick(elementId, category, action, label, {...additionalParams,  ...additionalContext});
};

/**
 * Tracks a custom event across both GA4 and Comscore.
 *
 * @param {string} eventName - The name of the event.
 * @param {object} [ga4EventParams] - Optional parameters specific to GA4 event.
 * @param {object} [comscoreEventProperties] - Optional properties specific to Comscore event.
 */
export const trackCustomEvent = (
  eventName: string,
  ga4EventParams?: { [key: string]: any },
  comscoreEventProperties?: { [key: string]: string | number }
) => {
  // Track custom event for GA4
  trackGA4Event(eventName, ga4EventParams);

  // Track custom event for Comscore
  trackComscoreEvent(eventName, comscoreEventProperties);
};

// You can also re-export updateComscoreLabels if you need to expose it
// for specific Comscore-only label updates (e.g., 1P data, consent).
